const API_BASE_URL: string = import.meta.env.VITE_API_URL || "";

function withBase(path: string): string {
  return `${API_BASE_URL.replace(/\/$/, "")}${path}`;
}

export type SchoolTier = "dream" | "realistic" | "safety";
export type GolfFitStatus = "missing" | "updating" | "ready" | "unavailable" | "failed" | "stale";

export interface GolfFitEvidence {
  playerClassRank: number | null;
  playerScoringDifferential: number | null;
  playerClassYear: number | null;
  playerEventCount?: number | null;
  playerTournamentStrength?: number | null;
  programAverageClassRank: number | null;
  programAverageScoringDifferential: number | null;
  programCommitCount: number | null;
}

export interface GolfFitAssessment {
  _id?: string;
  teamId?: string;
  schoolName?: string;
  status: GolfFitStatus;
  score: number | null;
  tier: SchoolTier | null;
  summary: string | null;
  explanation: string | null;
  assessedAt: string | null;
  inputHash?: string | null;
  error?: string | null;
  evidence?: GolfFitEvidence | null;
}

export interface SchoolFitSnapshot {
  schoolName: string;
  division?: string | null;
  gender?: string | null;
}

export async function fetchSchoolGolfFits(
  teamIds: string[],
  linkedJgsProfile?: string
): Promise<GolfFitAssessment[]> {
  const uniqueIds = [...new Set(teamIds.filter(Boolean))];
  if (uniqueIds.length === 0) return [];
  const url = new URL(withBase("/school-fits"));
  url.searchParams.set("teamIds", uniqueIds.join(","));
  if (linkedJgsProfile) url.searchParams.set("linkedJgsProfile", linkedJgsProfile);

  const response = await fetch(url.toString(), { credentials: "include" });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error || `Failed to load golf-fit assessments (${response.status})`);
  }
  const body = await response.json();
  return body.assessments || [];
}

export async function refreshSchoolGolfFit(
  teamId: string,
  school: SchoolFitSnapshot,
  linkedJgsProfile?: string
): Promise<GolfFitAssessment> {
  const url = new URL(withBase(`/school-fits/${encodeURIComponent(teamId)}`));
  if (linkedJgsProfile) url.searchParams.set("linkedJgsProfile", linkedJgsProfile);

  const response = await fetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(school),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error || `Failed to assess golf fit (${response.status})`);
  }
  return response.json();
}
